import {
  adaptNotification,
  adaptPdfStatusMessage,
  adaptReportRefreshMessage,
  adaptStatusUpdateMessage,
} from '@mathflat/domain/Notification'
import { domainProvider } from '@mathflat/shared/@common/domain/domainProvider'
import { jwtTokenService } from '@mathflat/shared/@modules/auth/jwtTokenService'
import { fetchEventSource } from '@microsoft/fetch-event-source'
import { useEffect, useRef } from 'react'

import { NotificationService } from '@/contentComponents/global/Notification'
import { LessonGroupService } from '@/page/Lesson/_service'
import ReportListService from '@/page/Lesson/Report/ReportList/ReportList.service'
import commonRepo from '@/repository/common.repo'
import { CustomEventService } from '@/service/customEvent.service'
import { reissueSingleFlightScheduler } from '@/utils/axios/jwtInterceptors'
import { envUtils } from '@/utils/envUtils'

import useDynamicRepo from './useDynamicRepo'
import { useFreshComponentRef } from './useFreshComponentRef'
import useNotificationSync from './useNotificationSync'
import useRepository from './useRepository'

class FatalError extends Error {}
class PermanentError extends FatalError {}

type SseEventType =
  | 'NOTIFICATION'
  | 'REPORT'
  | 'WORKBOOK_PDF'
  | 'CONTRACT_PDF'
  | 'ACHIEVEMENT_REPORT'
  | 'CUSTOM_SIGNATURE_WORKBOOK_THUMBNAIL'
  | 'CUSTOM_WORKBOOK_THUMBNAIL'

type SseEnvelope = {
  eventType: SseEventType
  version: number
  occurredAt: string
  payload: Record<string, unknown>
}

const getNotificationApiBaseUrl = () => {
  if (envUtils.isLive || localStorage.getItem('DEV_API') === 'live')
    return `https://notification-api.${domainProvider.TARGET_DOMAIN}`
  if (envUtils.isStaging || localStorage.getItem('DEV_API') === 'stg')
    return `https://notification-api-staging.${domainProvider.TARGET_DOMAIN}`
  return `https://notification-api-dev.${domainProvider.TARGET_DOMAIN}`
}

const getNotificationStreamUrl = () =>
  `${getNotificationApiBaseUrl()}/notifications/stream?platform=MATHFLAT`

const getJwt = () => jwtTokenService.getAccessToken()

const MAX_RETRY_COUNT = 5
const MAX_UNAUTHORIZED_COUNT = 3
const RECOVERY_DELAY = 30 * 60 * 1000

// 재시도할수록 대기 시간을 지수적으로 늘린다 (최대 30초). jitter(random * 1000)를 더해 동시에 끊긴 클라이언트들의 재연결이 분산되도록 한다.
const getBackoffDelay = (count: number) =>
  Math.min(1000 * 2 ** count, 30_000) + Math.random() * 1000

const useSSE = () => {
  useNotificationSync()

  const notificationService = useRepository(NotificationService)
  const lessonGroupService = useRepository(LessonGroupService)
  const reportListService = useDynamicRepo([ReportListService, lessonGroupService.groupKey])
  const reportLiveServiceRef = useFreshComponentRef(reportListService)
  const activeControllerRef = useRef<AbortController | null>(null)

  useEffect(() => {
    if (!commonRepo.user?.teacherId) return

    if (activeControllerRef.current && !activeControllerRef.current.signal.aborted) return

    let currentController: AbortController
    let shutdownTimer: ReturnType<typeof setTimeout> | null = null
    let recoveryTimer: ReturnType<typeof setTimeout> | null = null
    let canConnect = true
    let retryCount = 0
    let unauthorizedCount = 0

    // 모든 재시도를 소진했을 때 RECOVERY_DELAY 후 카운터를 초기화하고 재연결을 시도
    const scheduleRecovery = () => {
      recoveryTimer = setTimeout(() => {
        retryCount = 0
        unauthorizedCount = 0
        connect()
      }, RECOVERY_DELAY)
    }

    const connect = () => {
      if (!canConnect) return

      // 재연결 시 currentController가 재할당되므로, 클로저 혼선 방지를 위해 로컬에 캡처
      const controller = new AbortController()
      currentController = controller
      activeControllerRef.current = controller

      fetchEventSource(getNotificationStreamUrl(), {
        headers: {
          Authorization: `Bearer ${getJwt()}`,
        },
        signal: controller.signal,
        openWhenHidden: true,
        onopen: async (res) => {
          if (res.status === 401) {
            // 한계 초과 체크를 리이슈 전에 수행해 불필요한 네트워크 요청을 방지
            unauthorizedCount++
            if (unauthorizedCount > MAX_UNAUTHORIZED_COUNT) {
              throw new FatalError('SSE 401: 리이슈 최대 횟수 초과')
            }

            try {
              await reissueSingleFlightScheduler.start(() => jwtTokenService.reissueTokens())
            } catch {
              throw new FatalError('SSE 401: 토큰 재발급 실패')
            }

            // throw 대신 abort + connect() 재호출 — throw 시 라이브러리가 구 토큰으로 재시도하므로
            controller.abort()
            setTimeout(connect, 0)
            return
          }
          if (res.status === 403) {
            // 권한 문제는 시간이 지나도 해결되지 않으므로 재시도 없이 영구 중단
            throw new PermanentError('SSE 403: 접근 불가 플랫폼')
          }
          if (res.status >= 400) {
            throw new Error(`SSE open 실패: ${res.status}`)
          }

          // 연결 성공 — 카운터 초기화
          retryCount = 0
          unauthorizedCount = 0
          // await 하지 않으므로 실패가 처리되지 않은 프로미스 거부로 남는다.
          // 재연결 때마다 도는 경로라 실패해도 화면은 그대로 두고 경고만 남긴다.
          notificationService.fetchNotifications().catch((error) => {
            console.warn('알림 목록 동기화 실패', error)
          })
          notificationService.fetchUnreadCount().catch((error) => {
            console.warn('알림 안읽음 수 동기화 실패', error)
          })
        },
        onmessage: (e) => {
          if (e.event === 'shutdown') {
            // 서버 롤링 배포 신호. 정상 종료이므로 retryCount 패널티 없이 재연결
            controller.abort()
            retryCount = 0
            shutdownTimer = setTimeout(connect, e.retry ?? 0)
            return
          }

          if (!e.data) return

          let envelope: SseEnvelope
          try {
            envelope = JSON.parse(e.data) as SseEnvelope
          } catch {
            return
          }

          const { eventType, payload } = envelope

          try {
            switch (eventType) {
              case 'NOTIFICATION':
                // NOTIFICATION은 payload 자체가 알림이다 (다른 이벤트와 달리 message 래퍼 없음)
                notificationService.receiveNotification(adaptNotification(payload))
                break
              case 'REPORT':
                reportLiveServiceRef.current.requestRefetchReportListItemsById(
                  adaptReportRefreshMessage(payload.message),
                )
                break
              case 'WORKBOOK_PDF':
                CustomEventService.내교재PDF생성상태.dispatch(
                  adaptStatusUpdateMessage(payload.message),
                )
                break
              case 'CONTRACT_PDF':
                CustomEventService.스쿨플랫계약서생성상태.dispatch(
                  adaptPdfStatusMessage(payload.message),
                )
                break
              case 'ACHIEVEMENT_REPORT':
                CustomEventService.유형분석보고서생성상태.dispatch(
                  adaptPdfStatusMessage(payload.message),
                )
                break
              case 'CUSTOM_SIGNATURE_WORKBOOK_THUMBNAIL':
                CustomEventService.커스텀시그니처교재썸네일생성상태.dispatch(
                  adaptStatusUpdateMessage(payload.message),
                )
                break
              case 'CUSTOM_WORKBOOK_THUMBNAIL':
                CustomEventService.커스텀교재썸네일생성상태.dispatch(
                  adaptStatusUpdateMessage(payload.message),
                )
                break
            }
          } catch (err) {
            console.error('[useSSE] onmessage error:', eventType, err)
          }
        },
        onerror: (err) => {
          // 항상 throw해서 라이브러리 내부 재시도를 차단 (구 토큰으로 요청하기 때문에)
          // 모든 재시도는 아래 .catch()의 connect()를 통해서만 이루어지도록 강제
          throw err
        },
      }).catch((err) => {
        // SSE에서 발생한 모든 에러는 여기서 처리 (에러가 새어나가지 않아 앱크래시 방지)
        if (!canConnect) return
        // abort()를 직접 호출한 경우(401 재연결, shutdown) — connect()가 이미 예약되어 있음
        if (controller.signal.aborted) return
        if (err instanceof PermanentError) {
          // 403 등 재시도해도 의미없는 에러 — 영구 중단
          return
        }
        if (err instanceof FatalError) {
          // 복구 가능성이 있는 치명적 에러(401 한계 초과, 재발급 실패 등) — 30분 후 재시도
          scheduleRecovery()
          return
        }

        retryCount++
        if (retryCount > MAX_RETRY_COUNT) {
          // 재시도 횟수 소진 — 30분 후 재시도
          scheduleRecovery()
          return
        }
        setTimeout(connect, getBackoffDelay(retryCount))
      })
    }

    connect()

    return () => {
      canConnect = false
      currentController.abort()
      activeControllerRef.current = null
      if (shutdownTimer) clearTimeout(shutdownTimer)
      if (recoveryTimer) clearTimeout(recoveryTimer)
    }
  }, [commonRepo.user?.teacherId])
}

export default useSSE
